import SunnyLink, { ISunnyLinkProps } from '@/components/Common/SunnyLink';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

import styles from './BaseButton.module.scss';

export type BaseLabelButtonProps = {
  as: 'label';
  disabled?: boolean;
} & Omit<JSX.IntrinsicElements['label'], 'ref'>;

export type BaseLinkButtonProps = {
  as: 'link';
  disabled?: boolean;
} & Omit<ISunnyLinkProps, 'ref'>;

export type BaseRealButtonProps = {
  as: 'button';
} & Omit<JSX.IntrinsicElements['button'], 'ref'>;

export type BaseButtonProps = BaseLabelButtonProps | BaseLinkButtonProps | BaseRealButtonProps;

/**
 * `<a>`, `<button>`, `<label>`이 같은 형태일 때 사용하는 분기 컴포넌트.
 *
 * 표준 `<a>` 태그에는 없는 `disabled` 특성을 처리할 수 있다.
 */
const BaseButton: React.ForwardRefRenderFunction<HTMLElement, BaseButtonProps> = (
  { children, className, disabled, ...props },
  ref,
) => {
  // onClick을 밖에서 꺼내면, e.g. onClick = disabled ? undefined : props.onClick
  // MouseEventHandler<E>의 E가 섞여버리므로 안에서 꺼냄
  // 다른 것들도 마찬가지
  switch (props.as) {
    case 'button':
      return (
        <button
          {...props}
          ref={ref as any}
          className={clsx(styles.button, className)}
          disabled={disabled}
          onClick={disabled ? undefined : props.onClick}
        >
          {children}
        </button>
      );
    case 'label':
      return (
        <label
          {...props}
          ref={ref as any}
          className={clsx(styles.pseudo, className)}
          onClick={disabled ? undefined : props.onClick}
        >
          {children}
        </label>
      );
    default:
      return (
        <SunnyLink
          {...props}
          ref={ref as any}
          className={clsx(styles.pseudo, className)}
          href={disabled ? undefined : props.href}
          onClick={disabled ? undefined : props.onClick}
        >
          {children}
        </SunnyLink>
      );
  }
};

export default forwardRef(BaseButton);
